/**
 * Form for creating a status-log entry (notes or status-specific follow-up types).
 * @module LogNotes
 */

import { useEffect, useMemo, useState } from "react";
import { Select } from "risa-oasis-ui_v2";
import { BACKGROUND_COLORS_FOR_NOTES } from "../../../../../constants/backgroundColorsForNotes";
import { getStatusLogConfig } from "../../../../../constants/statusLogConfig";
import { FollowUpTypeOption } from "../../../../../data-model/followUpTypeModel";
import { StatusLogDataWithId } from "../../../../../data-model/statusLogModel";
import DashedLines from "../../../../../svg/dashedLines";
import { ADDED_NOTES_FOLLOW_UP_TYPE_ID } from "../../utils/statusLogHelpers";
import ColorSelectorForOrder from "./colorSelectorForOrder";
import { PreviousAddedNotes } from "./PreviousAddedNotes";

/** Props accepted by {@link LogNotes}. */
export interface LogNotesProps {
  /** Order status used to resolve follow-up types and templates. */
  status: string;
  /** Updates the free-text comment body in the parent. */
  setComments: (comments: string) => void;
  /** Updates the selected follow-up type in the parent. */
  setSelectedFollowUpType: (followUpType: {
    id: string;
    label: string;
  }) => void;
  /** Updates the notes colour selection in the parent. */
  setNotesColor: (color: string) => void;
  /** Hex colour pre-selected in the colour picker. */
  preSelectedNotesColor: string;
  /** Prior "Added notes" entries shown below the create form. */
  previousNotes: StatusLogDataWithId[];
  /** True while prior notes are still loading. */
  isLoadingPreviousNotes: boolean;
  /** True when the prior-notes fetch failed. */
  hasPreviousNotesError?: boolean;
}

/**
 * Renders the create form for the Status Logs Notes tab.
 *
 * When the only available follow-up type is "Added notes", shows a simplified
 * notes textarea. Otherwise shows follow-up type select + optional templates.
 *
 * @param props - Status, comment/colour setters, and colour preselection
 */
export const LogNotes = (props: LogNotesProps) => {
  const [selectedTemplates, setSelectedTemplates] = useState<number[]>([]);
  const [manualComment, setManualComment] = useState<string>("");
  const templates: string[] = useMemo(() => {
    return getStatusLogConfig(props.status).templates as unknown as string[];
  }, [props.status]);

  const followUpTypeOptions: FollowUpTypeOption[] = useMemo(() => {
    return getStatusLogConfig(props.status)
      .followUpTypes as unknown as FollowUpTypeOption[];
  }, [props.status]);

  const isNotesOnlyMode = useMemo(() => {
    return (
      followUpTypeOptions.length === 1 &&
      followUpTypeOptions[0]?.value === ADDED_NOTES_FOLLOW_UP_TYPE_ID
    );
  }, [followUpTypeOptions]);

  const shouldShowTemplates = useMemo(() => {
    return !isNotesOnlyMode && templates && templates.length > 0;
  }, [isNotesOnlyMode, templates]);

  useEffect(() => {
    if (isNotesOnlyMode && followUpTypeOptions[0]) {
      props.setSelectedFollowUpType({
        id: followUpTypeOptions[0].value,
        label: followUpTypeOptions[0].label,
      });
    }
  }, [isNotesOnlyMode, followUpTypeOptions, props.setSelectedFollowUpType]);

  const handleDeselectTemplate = (index: number) => {
    const newSelectedTemplates = selectedTemplates.filter((i) => i !== index);
    setSelectedTemplates(newSelectedTemplates);
    const remainingComments = newSelectedTemplates
      .map((i) => templates[i])
      .join(", ");
    props.setComments(remainingComments);
  };

  const handleSelectTemplate = (index: number) => {
    const newSelectedTemplates = [...selectedTemplates, index];
    setSelectedTemplates(newSelectedTemplates);
    const allComments = newSelectedTemplates
      .map((i) => templates[i])
      .join(", ");
    props.setComments(allComments);
    setManualComment("");
  };

  const handleFollowUpTypeChange = (selectedValue: { value: string }) => {
    const selectedOption = followUpTypeOptions.find(
      (option) => option.value === selectedValue.value,
    );
    if (selectedOption) {
      props.setSelectedFollowUpType({
        id: selectedOption.value,
        label: selectedOption.label,
      });
    }
  };

  return (
    <div className="status-log--log-event flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pb-4">
        {!isNotesOnlyMode && (
          <Select
            id="status-log-select"
            onOptionChange={handleFollowUpTypeChange}
            label="Follow Up Type"
            placeholder="Select the follow up type"
            options={followUpTypeOptions}
          />
        )}

        <div className="status-log--body--follow-up-type flex w-full flex-col gap-3">
          {shouldShowTemplates && (
            <div className="status-log--body--follow-up-type--label w-full text-h11 font-bold text-primaryGray-1">
              Select a Template
            </div>
          )}
          {shouldShowTemplates &&
            templates.map((template, index) => (
              <div
                key={index}
                className={`status-log--body--follow-up-type--templates flex w-full cursor-pointer flex-col gap-2 rounded rounded-s bg-primaryGray-16 p-3 transition-all duration-200 ${
                  selectedTemplates.includes(index)
                    ? "border-1 border-primaryGray-1 text-primaryGray-1"
                    : "border-1 border-transparent hover:border-gray-300"
                }`}
                onClick={() => {
                  if (selectedTemplates.includes(index)) {
                    handleDeselectTemplate(index);
                  } else {
                    handleSelectTemplate(index);
                  }
                }}
              >
                <div className="flex w-full text-wrap text-primaryGray-3">
                  {template}
                </div>
              </div>
            ))}
        </div>

        {selectedTemplates.length === 0 && shouldShowTemplates && (
          <div className="status-log--body--follow-up-type--templates flex w-full flex-row items-center gap-2">
            <DashedLines />
            <div className="text-h11 font-normal text-primaryGray-1">Or</div>
            <DashedLines />
          </div>
        )}

        {selectedTemplates.length === 0 && (
          <div className="comments-section w-full">
            <div className="comments-section header mb-2 text-[0.75rem] font-normal leading-[1.125rem] text-primaryGray-2">
              {isNotesOnlyMode ? "Added Notes" : "Enter Manually"}
            </div>
            <div className="comments-section comments-textarea">
              <textarea
                className="h-[4.875rem] w-full resize-none self-stretch rounded-[0.25rem] border border-primaryGray-14 px-3 py-[0.875rem] text-[0.875rem] font-normal leading-[1.125rem] placeholder:text-[0.875rem] placeholder:font-normal placeholder:leading-[1.125rem] placeholder:text-primaryGray-9"
                placeholder={
                  isNotesOnlyMode
                    ? "Enter your notes here..."
                    : "Enter your general comments here"
                }
                value={manualComment}
                onChange={(e) => {
                  setManualComment(e.target.value);
                  props.setComments(e.target.value);
                  if (e.target.value.trim() && selectedTemplates.length > 0) {
                    setSelectedTemplates([]);
                  }
                }}
              />
            </div>
          </div>
        )}

        <PreviousAddedNotes
          notes={props.previousNotes}
          isLoading={props.isLoadingPreviousNotes}
          hasError={props.hasPreviousNotesError}
        />
      </div>

      <div className="shrink-0 border-t border-primaryGray-14 bg-white pt-4">
        <ColorSelectorForOrder
          backgroundColors={BACKGROUND_COLORS_FOR_NOTES}
          onColorSelect={props.setNotesColor}
          preSelectedNotesColor={props.preSelectedNotesColor ?? "transparent"}
        />
      </div>
    </div>
  );
};
